import { getPokerAudioFilesToPreload } from '../Components/GameRoom/Presentation/PokerAudioPresetConfig';

let audioContext: AudioContext | null = null;
const audioBuffers: { [key: string]: AudioBuffer } = {};
let isMuted = false;
let volume = 1.0;
let preloadRequested = false;
let preloadPromise: Promise<void> | null = null;
let activeSources: Set<AudioBufferSourceNode> = new Set();
let channelSources: Map<string, AudioBufferSourceNode> = new Map();
let channelVersions: Map<string, number> = new Map();
const audioBufferLoads: { [key: string]: Promise<AudioBuffer> } = {};
let unlockListenersInstalled = false;
const USER_INTERACTION_EVENTS = ['click', 'touchstart', 'keydown', 'mousedown'];
const USER_INTERACTION_CAPTURE = true;
const AUDIO_FILES = getPokerAudioFilesToPreload();

export const initAudioContext = () => {
    if (audioContext) {
        return;
    }

    setupUserInteractionListeners();
    preloadRequested = true;
};

const unlockAudioContext = () => {
    if (audioContext) {
        void resumeAudioContext();
        removeUserInteractionListeners();
        return;
    }

    try {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        void resumeAudioContext();
        removeUserInteractionListeners();

        if (preloadRequested) {
            void preloadAudios();
        }
    } catch (error) {
        console.error('Failed to initialize AudioContext:', error);
    }
};

const setupUserInteractionListeners = () => {
    if (audioContext || unlockListenersInstalled || typeof document === 'undefined') {
        return;
    }

    USER_INTERACTION_EVENTS.forEach(eventType => {
        document.addEventListener(eventType, unlockAudioContext, { capture: USER_INTERACTION_CAPTURE, once: true });
    });
    unlockListenersInstalled = true;
};

const removeUserInteractionListeners = () => {
    if (typeof document === 'undefined') {
        return;
    }

    USER_INTERACTION_EVENTS.forEach(eventType => {
        document.removeEventListener(eventType, unlockAudioContext, USER_INTERACTION_CAPTURE);
    });
    unlockListenersInstalled = false;
};

const resumeAudioContext = async () => {
    if (!audioContext) {
        return;
    }

    if (audioContext.state === 'suspended') {
        await audioContext.resume().catch(e => {
            console.warn('Failed to resume AudioContext:', e);
        });
    }
};

const ensureAudioContext = (): AudioContext | null => {
    if (!audioContext) {
        setupUserInteractionListeners();
        return null;
    }

    void resumeAudioContext();
    return audioContext;
};

export const setMuted = (muted: boolean) => {
    isMuted = muted;
    if (isMuted) {
        stopAllAudio();
    }
};

export const getMuted = () => {
    return isMuted;
};

export const setVolume = (newVolume: number) => {
    volume = Math.max(0, Math.min(1, newVolume));
};

export const getVolume = () => {
    return volume;
};

const stopAllAudio = () => {
    try {
        activeSources.forEach((src) => {
            try { src.stop(0); } catch (_) {}
        });
        activeSources.clear();
        channelSources.clear();
        channelVersions.clear();
    } catch (_) {}
};

export const stopAudioChannel = (channel: string) => {
    const source = channelSources.get(channel);
    if (source) {
        try { source.stop(0); } catch (_) {}
        activeSources.delete(source);
        channelSources.delete(channel);
    }
    channelVersions.set(channel, (channelVersions.get(channel) || 0) + 1);
};

const loadAudio = async (url: string): Promise<AudioBuffer> => {
    const context = ensureAudioContext();
    if (!context) {
        throw new Error('AudioContext not available');
    }
    
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    return await context.decodeAudioData(arrayBuffer);
};

const loadAudioFile = (name: string): Promise<AudioBuffer> => {
    if (audioBuffers[name]) {
        return Promise.resolve(audioBuffers[name]);
    }

    if (!audioBufferLoads[name]) {
        audioBufferLoads[name] = loadAudio(`${import.meta.env.BASE_URL}audio/${name}`)
            .then((buffer) => {
                audioBuffers[name] = buffer;
                delete audioBufferLoads[name];
                return buffer;
            })
            .catch((error) => {
                delete audioBufferLoads[name];
                throw error;
            });
    }

    return audioBufferLoads[name];
};

export const preloadAudios = async () => {
    preloadRequested = true;
    initAudioContext();

    if (!audioContext) {
        return;
    }

    const context = ensureAudioContext();
    if (!context) {
        return;
    }

    const missingFiles = AUDIO_FILES.filter(file => !audioBuffers[file]);
    if (missingFiles.length === 0) {
        return;
    }

    if (!preloadPromise) {
        preloadPromise = (async () => {
            await Promise.all(missingFiles.map(async (file) => {
                try {
                    await loadAudioFile(file);
                } catch (error) {
                    console.error(`Failed to load audio file: ${file}`, error);
                }
            }));
        })().finally(() => {
            preloadPromise = null;
        });
    }

    await preloadPromise;
};

export interface AudioPlayOptions {
    channel?: string;
    offsetSeconds?: number;
    durationSeconds?: number;
}

const prepareAudioChannel = (channel: string | undefined): number | undefined => {
    if (!channel) return undefined;
    stopAudioChannel(channel);
    return channelVersions.get(channel);
};

const isCurrentAudioChannelRequest = (channel: string | undefined, version: number | undefined): boolean => {
    return !channel || channelVersions.get(channel) === version;
};

const playLoadedAudio = (name: string, options: AudioPlayOptions = {}, channelVersion?: number) => {
    if (!audioBuffers[name]) {
        return;
    }

    if (!isCurrentAudioChannelRequest(options.channel, channelVersion)) {
        return;
    }

    internalPlay(name, options);
};

const loadAndPlayAudio = (name: string, options: AudioPlayOptions = {}, channelVersion?: number) => {
    loadAudioFile(name).then(() => {
        if (!isMuted) {
            playLoadedAudio(name, options, channelVersion);
        }
    }).catch(error => {
        console.error(`Failed to load audio file: ${name}`, error);
    });
};

const internalPlay = (name: string, options: AudioPlayOptions = {}) => {
    const context = ensureAudioContext();
    if (!context) {
        console.warn('Cannot play audio: AudioContext not available');
        return;
    }

    try {
        const source = context.createBufferSource();
        const gainNode = context.createGain();

        source.buffer = audioBuffers[name];
        source.connect(gainNode);
        gainNode.connect(context.destination);

        gainNode.gain.value = volume;
        const offsetSeconds = Math.max(0, Math.min(options.offsetSeconds || 0, source.buffer.duration));
        const remainingDurationSeconds = source.buffer.duration - offsetSeconds;
        const durationSeconds = typeof options.durationSeconds === 'number'
            ? Math.max(0, Math.min(options.durationSeconds, remainingDurationSeconds))
            : undefined;

        if (typeof durationSeconds === 'number') {
            if (durationSeconds <= 0) return;
            source.start(0, offsetSeconds, durationSeconds);
        } else {
            source.start(0, offsetSeconds);
        }
        activeSources.add(source);
        if (options.channel) {
            channelSources.set(options.channel, source);
        }

        source.onended = () => {
            activeSources.delete(source);
            if (options.channel && channelSources.get(options.channel) === source) {
                channelSources.delete(options.channel);
            }
        };
    } catch (e) {
        console.error(`Error playing audio ${name}:`, e);
    }
};

export const playAudio = (name: string, options: AudioPlayOptions = {}) => {
    if (isMuted) return;

    const channelVersion = prepareAudioChannel(options.channel);
    const context = ensureAudioContext();
    if (!context) {
        preloadRequested = true;
        return;
    }

    if (!audioBuffers[name]) {
        loadAndPlayAudio(name, options, channelVersion);
        return;
    }

    playLoadedAudio(name, options, channelVersion);
};
